import { Box, Button, Dialog, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch } from "src/store";
import * as yup from "yup";

import { nanoid } from "@reduxjs/toolkit";
import { useRouter } from "next/router";
import { getUserDetails } from "src/slices/articles/thunks";
import { languages, locations, usStates } from "src/slices/brief/helpers";
import { createBrief } from "src/slices/brief/thunks";
import { optimizeArticle } from "src/slices/optimizeContent/thunks";
import { createProject } from "src/slices/projects";
import { snackAdded } from "src/slices/system";
import { updateUserOnboarding } from "src/slices/system/thunks";
import { ChooseArticleType } from "./ChooseArticleType";
import { Article } from "./article";
import { Confirmation } from "./confirmation";
import Success from "./success";

interface Props {
  isEEATMode?: boolean;
  articleLink?: string;
  onClose?: (briefId: string) => void;
}

export const Onboarding = ({ onClose, isEEATMode, articleLink }: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(isEEATMode ? 1 : 0);
  const [open, setOpen] = useState(true);
  const [articleChoice, setArticleChoice] = useState(
    isEEATMode ? "update" : "write"
  );
  const [briefId, setBriefId] = useState("");

  const [confirmation, setConfirmation] = useState("");
  const [projectId, setProjectId] = useState("");
  const id = nanoid();

  //get locations and state
  let location = locations[206]; // Default US location
  let state = null;

  try {
    const savedLocation = localStorage.getItem("brief-location");
    if (savedLocation) {
      location = JSON.parse(savedLocation) || location;
    }
  } catch (error) {}

  try {
    state = JSON.parse(localStorage.getItem("brief-state"));
    // Validate state is only used when location is United States
    if (state && location?.location_name !== "United States") {
      state = null;
      localStorage.removeItem("brief-state");
    }
  } catch (error) {}

  const formik = useFormik<{
    keyword: string;
    location: (typeof locations)[0];
    state: (typeof usStates)[0] | null;
    language: string;
    websiteUrl: string;
    generateArticle: boolean;
    interlinking: boolean;
    stats: boolean;
    insights: string;
    articleLink: string;
    humanize: boolean;
    thoughtLeadership: boolean;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
    generateAutomatedInsights: boolean;
  }>({
    initialValues: {
      keyword: "",
      location,
      state,
      language: "English",
      websiteUrl: "",
      generateArticle: true,
      interlinking: true,
      stats: false,
      insights: "",
      articleLink: articleLink || "",
      humanize: false,
      thoughtLeadership: false,
      aiResearchAgent: true,
      aiEvalRefineAgent: true,
      generateAutomatedInsights: false,
    },

    onSubmit: async (values) => {
      try {
        formik.setSubmitting(true);

        // Add visual feedback for the process
        dispatch(snackAdded({
          message: "Initializing...",
          type: "info",
          duration: 2000,
        }));

        const languageCode = languages.find(
          (value) => value.languageName === formik.values.language
        ).languageCode;

        // Use state location name if US state is selected, otherwise use country
        const locationName =
          formik.values.state?.location_name ||
          formik.values.location.location_name;
        let newProjectId = "";
        if (!projectId) {
          dispatch(snackAdded({
            message: "Creating project...",
            type: "info",
            duration: 2000,
          }));

          const resCreateProject = await dispatch(
            createProject({
              projectName: values.websiteUrl,
              projectDomain: values.websiteUrl,
              AImodel: "claude",
            })
          );
          newProjectId = resCreateProject?.payload?.data?.projectId;

          if (newProjectId) {
            localStorage.setItem(
              "brief-project",
              JSON.stringify(resCreateProject?.payload?.data?.projectId)
            );
            dispatch(snackAdded({
              message: "Project created successfully",
              type: "success",
              duration: 2000,
            }));
          } else {
            throw new Error("Failed to create project");
          }
        }

        if (articleChoice === "write") {
          dispatch(snackAdded({
            message: "Setting up brief...",
            type: "info",
            duration: 2000,
          }));

          const brief = await dispatch(
            createBrief({
              languageCode,
              keyword: formik.values.keyword,
              locationName: locationName,
              languageName: formik.values.language,
              projectId: projectId ? projectId : newProjectId,

              projectName: values.websiteUrl,
              seoMissionsBriefId: "",
              isOnboardingBrief: true,
              generateArticle: values.generateArticle,
              GSCsiteUrl: "",
              insights: formik.values.insights,
              generateAutomatedInsights: formik.values.generateAutomatedInsights,
              stats: false,
              interlinking: formik.values.interlinking,
              companyDetails: true,
              humanize: true,
              thoughtLeadership: false,
              aiResearchAgent: formik.values.aiResearchAgent,
              aiEvalRefineAgent: formik.values.aiEvalRefineAgent,
            })
          );

          if ((brief as unknown as { payload: { id: string } })?.payload?.id) {
            setBriefId(
              (brief as unknown as { payload: { id: string } }).payload.id
            );

            if (values.generateArticle) {
              dispatch(snackAdded({
                message: "Initializing content generation...",
                type: "success",
                duration: 2000,
              }));
            } else {
              dispatch(snackAdded({
                message: "Brief created successfully! Redirecting to advanced mode...",
                type: "success",
                duration: 2000,
              }));
            }
          }

          setCurrentStep(3);
          await dispatch(updateUserOnboarding());
        } else {
          //update article
          dispatch(snackAdded({
            message: "Preparing article update...",
            type: "info",
            duration: 2000,
          }));

          const userDetails = (await dispatch(getUserDetails())).payload?.user;
          formik.setFieldValue("keyword", "");

          dispatch(snackAdded({
            message: "Analyzing existing content...",
            type: "info",
            duration: 2000,
          }));

          let optimizedContent = await dispatch(
            optimizeArticle({
              id: id,
              userId: userDetails.Username,
              keyword: formik.values.keyword,
              articleLink: formik.values.articleLink,
              location: formik.values.location,
              languageCode,
              languageName: formik.values.language,
              projectId: projectId ? projectId : newProjectId,
              projectName: values.websiteUrl,
              seoMissionsBriefId: "",
              insights: formik.values.insights,
              stats: formik.values.stats,
              interlinking: formik.values.interlinking,
              humanize: true,
              competitors: [],
              GSCsiteUrl: "",
              excludedLinks: [],
              aiResearchAgent: true,
              aiEvalRefineAgent: formik.values.aiEvalRefineAgent,
              companyDetails: "",
              projectDomain: values.websiteUrl,
              isOnboardingArticle: true,
            })
          );

          if (optimizedContent.payload.status_code === 200) {
            dispatch(snackAdded({
              message: "Article update process initiated successfully!",
              type: "success",
              duration: 3000,
            }));
            formik.setFieldValue("keyword", "");
            setCurrentStep(3);
            // onCreated(id);
          } else {
            dispatch(snackAdded({
              message: "Failed to start article update. Please try again.",
              type: "error",
              duration: 3000,
            }));
          }
        }
      } catch (error) {
        console.error("Onboarding submission error:", error);
        dispatch(snackAdded({
          message: "An error occurred during setup. Please try again.",
          type: "error",
          duration: 3000,
        }));
      } finally {
        formik.setSubmitting(false);
      }
    },
    validationSchema: yup.object({
      keyword: yup.string().max(255).required("Focus keyword is required"),
      articleLink:
        articleChoice === "update"
          ? yup.string().required("Article link is required")
          : yup.string().nullable(),
      websiteUrl: yup
        .string()
        .matches(
          /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/,
          "Enter correct website url"
        )
        .required("Website url is required"),
      location: yup.object().required("Location is required").nullable(),
    }),
  });

  const handleOnClose = () => {
    if (currentStep === 3 && articleChoice === "update") {
      router.push("/contentUpdate");
    }

    formik.setFieldValue("generateArticle", false);
    formik.setFieldValue("keyword", "");
    formik.setFieldValue("articleLink", "");
    formik.setFieldValue("websiteUrl", "");

    setCurrentStep(0);
    setOpen(false);
    onClose?.(briefId);
    dispatch(updateUserOnboarding());
  };

  useEffect(() => {
    if (confirmation === "advanced") {
      formik.setFieldValue("generateArticle", false);
      formik.submitForm();
    }
    if (confirmation === "continue") {
      formik.setFieldValue("generateArticle", true);
      formik.submitForm();
    }
  }, [confirmation]);

  const componentsMap = {
    0: (
      <ChooseArticleType
        getArticleChoice={(value: string) => [setArticleChoice(value)]}
      />
    ),
    1: (
      <Article
        formik={formik}
        articleChoice={articleChoice}
        isEEATMode={isEEATMode}
        isSubmitted={(type, projectId) => {
          setProjectId(projectId);
          type == "update"
            ? formik.submitForm()
            : type == "advanced"
            ? [
                formik.setFieldValue("generateArticle", false),
                formik.submitForm(),
              ]
            : setCurrentStep(2);
        }}
        onClose={handleOnClose}
        onBack={() => [setCurrentStep(0), setArticleChoice("write")]}
      />
    ),
    2: (
      <Confirmation
        getConfirmation={(value: string) => setConfirmation(value)}
        onClose={handleOnClose}
      />
    ),

    3: (
      <Success
        onClose={handleOnClose}
        articleChoice={articleChoice}
        isOneClick={confirmation === "continue" ? true : false}
      />
    ),
    // 3: <Error onClose={handleOnClose} />,
  };
  return (
    <Dialog
      // fullWidth
      maxWidth="lg"
      open={open}
      // onClose={handleOnClose}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, mt: 5 }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "8px",
          borderRadius: "4px 4px 0px 0px",
          background:
            "linear-gradient(60.66deg, #FFD875 -3.87%, #DA00C8 26.08%, #6A00FF 56.49%)",
        }}
      />

      <Box
        sx={{
          borderRadius: "0px 0px 8px 8px",
          background: "#FFFFFF",

          /* Wrapper Shadow */
          boxShadow: "0px 1px 2px 0px rgba(100, 116, 139, 0.10)",
        }}
        display="flex"
        padding={currentStep == 0 ? "24px" : "0px"}
        // paddingTop={currentStep == 0 ? "0px" : "24px"}
        paddingLeft={currentStep == 1 ? "24px" : "20px"}
        flexDirection="column"
        alignItems="flex-start"
        gap="16px"
        alignSelf="stretch"
        //   width={width}
      >
        {currentStep == 0 && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              alignSelf="stretch"
            >
              <Typography
                color="#121828"
                /* Heading/H1 */
                fontFamily="Inter"
                fontSize="20px"
                fontStyle="normal"
                fontWeight="500"
                lineHeight="normal"
              >
                Welcome!
              </Typography>
              {currentStep == 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="16px"
                  flexDirection="row"
                >
                  <Box width="100px">
                    <Typography
                      fontFamily="Inter"
                      fontSize="14px"
                      fontStyle="normal"
                      fontWeight="400"
                      color="#7B85AD"
                    >
                      Step {currentStep + 1} of 2
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      pt: "0px !important",
                      // width: "100%",
                    }}
                  >
                    <Typography
                      width="19.267px"
                      height="19.267px"
                      flexShrink="0"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => handleOnClose()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M2.69203 15.6063L8.66481 9.6335L2.69203 3.66072C2.46324 3.43568 2.33378 3.12787 2.33228 2.80725C2.33153 2.48588 2.45872 2.17807 2.686 1.95077C2.91329 1.72423 3.2211 1.59705 3.54248 1.59855C3.86385 1.60006 4.17091 1.72951 4.39595 1.95906L10.3664 7.93183L16.3392 1.95906C16.5642 1.72951 16.8713 1.60006 17.1927 1.59855C17.5133 1.59705 17.8218 1.72424 18.0491 1.95077C18.2764 2.17807 18.4036 2.48588 18.4021 2.80725C18.4014 3.12787 18.2719 3.43568 18.0431 3.66072L12.068 9.6335L18.0408 15.6063C18.2696 15.8313 18.399 16.1391 18.4005 16.4597C18.4013 16.7811 18.2741 17.0889 18.0468 17.3162C17.8195 17.5428 17.5117 17.6699 17.1903 17.6684C16.869 17.6669 16.5619 17.5375 16.3369 17.3079L10.3664 11.3352L4.39364 17.3079C3.9225 17.7723 3.16537 17.7693 2.69723 17.3027C2.22986 16.8353 2.2261 16.0782 2.68971 15.6063L2.69203 15.6063Z"
                          fill="#7B85AD"
                        />
                      </svg>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box alignSelf="stretch">
              <Typography
                fontFamily="Inter"
                fontSize="14px"
                fontStyle="normal"
                fontWeight="400"
                color="#7B85AD"
              >
                Choose if you would like to create an article or update your
                existing article
                {/* {descriptionText} */}
              </Typography>
            </Box>
          </>
        )}

        <form onSubmit={formik.handleSubmit}>{componentsMap[currentStep]}</form>
      </Box>
      {currentStep == 0 && (
        <Box sx={{ pt: 2, pb: 2, pr: 2, textAlign: "right" }}>
          <Button
            variant="contained"
            type="submit"
            onClick={() => {
              setCurrentStep(1);
            }}
          >
            Next step
          </Button>
        </Box>
      )}
    </Dialog>
  );
};
